import Underline from "@tiptap/extension-underline";
import { useContext, useEffect } from "react";
import { useEditor, EditorEvents } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import ListItem from "@tiptap/extension-list-item";
import FontFamily from "@tiptap/extension-font-family";
import Paragraph from "@tiptap/extension-paragraph";

import { useCertificateStore } from "@/stores";
import { useToastContext } from "@components/common/ui/Toast/Context";
import { ProductContext } from "@contexts/product/ProductContext";
import { OrientationType } from "../CertificateEditor.types";

export const useCertificateEditor = () => {
  const { productUuid } = useContext(ProductContext);
  const {
    updateCertificate,
    updateStore,
    restoreEditor,
    updateThemeColorDraftRevert,
    content,
    isEditing,
    isEditingContent,
    isDownload,
    primaryColor,
    secondaryColor,
    thirdColor,
    courseDuration,
  } = useCertificateStore();
  const toast = useToastContext();

  const handleChangeOrientation = async (orientation: OrientationType) => {
    await updateCertificate(productUuid, { orientation });
  };

  const handleThemeColor = (colorKey: string, color: string) => {
    updateStore({ [colorKey]: color });
  };

  const handleEnableEditing = () => {
    updateStore({ isEditing: !isEditing });
  };

  const handleRestore = async () => {
    updateStore({ isEditingContent: true });
    await restoreEditor(productUuid);
    updateStore({ isEditingContent: false });
    toast({ type: "success", title: "Sucesso", description: `Personalização concluida.` });
  };

  const handleCancelEditing = () => {
    updateThemeColorDraftRevert();
    updateStore({ isEditing: !isEditing });
  };

  const onSave = async () => {
    await updateCertificate(productUuid, { primaryColor, secondaryColor, thirdColor });
    await updateStore({
      isEditing: !isEditing,
      primaryColorDraft: primaryColor,
      secondaryColorDraft: secondaryColor,
      thirdColorDraft: thirdColor,
    });
    toast({ type: "success", title: "Sucesso", description: `Personalização concluida.` });
    updateStore({ isEditing: !isEditing });
  };

  const handleContent = (content: string) => {
    updateCertificate(productUuid, { content });
  };

  const CustomParagraph = Paragraph.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        id: {
          default: "",
          renderHTML: attributes => {
            return attributes.id ? { id: `${attributes.id}` } : null;
          },
        },
        class: {
          default: "",
          renderHTML: attributes => {
            return attributes.class ? { class: `${attributes.class}` } : null;
          },
        },
      };
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      FontFamily,
      Color.configure({ types: [TextStyle.name, ListItem.name] }),
      TextStyle,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      CustomParagraph,
    ],
    content: content,
    editorProps: {
      attributes: {
        class: "focus:outline-none",
      },
    },
    onUpdate(props: EditorEvents["update"]) {
      handleContent(props.editor.getHTML());
    },
  });

  editor?.on("transaction", () => {
    if (
      [
        "com carga horária de [Horas do Curso] hora(s).",
        "com carga horária de hora(s).",
        "com carga horária de " + courseDuration + " hora(s).",
      ].includes(editor?.state.selection.$from.node().textContent)
    ) {
      editor?.setEditable(false, false);
    } else {
      editor?.setEditable(true, false);
    }
  });

  useEffect(() => {
    if (content && isEditingContent) editor?.commands.setContent(content);
  }, [content, editor, isEditingContent]);

  useEffect(() => {
    editor?.setEditable(!isDownload || false, false);
  }, [isDownload, editor]);

  const changeCourseDuration = async (oldCourseDuration: string | undefined, newCourseDuration: string | undefined) => {
    let newContent = null;
    const textDefault = "com carga horária de [Horas do Curso] hora(s).";
    const textEmpty = "com carga horária de hora(s).";
    const textOld = "com carga horária de " + oldCourseDuration + " hora(s).";
    const textNew = "com carga horária de " + newCourseDuration + " hora(s).";

    if (content?.includes(textDefault)) {
      newContent = content?.replaceAll(textDefault, textNew);
    }
    if (content?.includes(textEmpty)) {
      newContent = content?.replaceAll(textEmpty, textNew);
    }
    if (content?.includes(textOld)) {
      newContent = content?.replaceAll(textOld, textNew);
    }
    if (content?.includes(textNew)) {
      newContent = content?.replaceAll(textNew, textNew);
    }

    if (newContent) {
      await updateStore({ isEditingContent: true });
      await handleContent(newContent);
      await updateStore({ isEditingContent: false });
    }
  };

  return {
    handleChangeOrientation,
    handleThemeColor,
    handleEnableEditing,
    handleCancelEditing,
    handleRestore,
    changeCourseDuration,
    onSave,
    editor,
  };
};
